/**
 * Monaco Editor 主题切换器组件
 *
 * 提供主题选择和切换功能，支持浅色/深色模式
 */

import type React from 'react'
import { useEffect, useState } from 'react'
import { useMonacoTheme, useThemeIcon } from '@/hooks/use-monaco-theme'

export interface ThemeToggleProps {
  /** 主题变化回调 */
  onThemeChange?: (theme: string) => void
  /** 是否显示标签 */
  showLabel?: boolean
  /** 标签文本 */
  label?: string
  /** 自定义类名 */
  className?: string
  /** 切换器类型 */
  type?: 'toggle' | 'dropdown' | 'switch'
  /** 尺寸 */
  size?: 'sm' | 'md' | 'lg'
  /** 是否禁用 */
  disabled?: boolean
}

/**
 * 简单的切换按钮组件
 */
const SimpleToggle: React.FC<{
  isDark: boolean
  onToggle: () => void
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
}> = ({ isDark, onToggle, disabled, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-4',
    md: 'w-11 h-6',
    lg: 'w-14 h-8',
  }

  const thumbClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-6 h-6',
  }

  return (
    <button
      type='button'
      onClick={onToggle}
      disabled={disabled}
      className={`
        relative inline-flex shrink-0 cursor-pointer rounded-full
        border-2 border-transparent transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        ${sizeClasses[size]}
        ${isDark ? 'bg-blue-600' : 'bg-gray-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none relative inline-block rounded-full
          bg-white shadow-lg transform ring-0 transition-transform duration-200 ease-in-out
          ${thumbClasses[size]}
          ${isDark ? `translate-x-${size === 'sm' ? '4' : size === 'md' ? '5' : '6'}` : 'translate-x-0'}
        `}
      >
        <span
          className={`
            absolute inset-0 flex h-full w-full items-center justify-center transition-opacity
            ${isDark ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200'}
          `}
        >
          <svg className={thumbClasses[size]} viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
            <title>Sun icon</title>
            <path
              fillRule='evenodd'
              d='M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z'
              clipRule='evenodd'
            />
          </svg>
        </span>
        <span
          className={`
            absolute inset-0 flex h-full w-full items-center justify-center transition-opacity
            ${isDark ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100'}
          `}
        >
          <svg className={thumbClasses[size]} viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
            <title>Moon icon</title>
            <path d='M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z' />
          </svg>
        </span>
      </span>
    </button>
  )
}

/**
 * 下拉选择器组件
 */
const ThemeDropdown: React.FC<{
  currentTheme: string
  availableThemes: Array<{ name: string; label: string; description: string }>
  onThemeChange: (theme: string) => void
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
}> = ({ currentTheme, availableThemes, onThemeChange, disabled, size = 'md' }) => {
  const [isOpen, setIsOpen] = useState(false)

  const sizeClasses = {
    sm: 'px-3 py-1 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-5 py-3 text-lg',
  }

  const selectedTheme = availableThemes.find(theme => theme.name === currentTheme)

  return (
    <div className='relative inline-block text-left'>
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          inline-flex items-center justify-between w-full rounded-lg border
          border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800
          text-gray-900 dark:text-gray-100 shadow-sm
          hover:bg-gray-50 dark:hover:bg-gray-700
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          transition-colors duration-200
          ${sizeClasses[size]}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <div className='flex items-center gap-2'>
          <span className='text-lg'>{currentTheme.includes('dark') ? '🌙' : '☀️'}</span>
          <span className='font-medium'>{selectedTheme?.label || 'Select Theme'}</span>
        </div>

        <svg
          className={`ml-2 -mr-1 h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 20 20'
          fill='currentColor'
          aria-hidden='true'
        >
          <title>Dropdown arrow</title>
          <path
            fillRule='evenodd'
            d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
            clipRule='evenodd'
          />
        </svg>
      </button>

      {isOpen && (
        <div className='absolute z-10 mt-1 w-full rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600'>
          <div className='max-h-60 overflow-y-auto'>
            {availableThemes.map(theme => (
              <button
                key={theme.name}
                type='button'
                onClick={() => {
                  onThemeChange(theme.name)
                  setIsOpen(false)
                }}
                className={`
                  w-full px-3 py-2 text-left
                  hover:bg-gray-100 dark:hover:bg-gray-700
                  focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700
                  transition-colors duration-150
                  ${theme.name === currentTheme ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}
                `}
              >
                <div className='flex items-center gap-2'>
                  <span className='text-lg'>{theme.name.includes('dark') ? '🌙' : '☀️'}</span>
                  <div>
                    <div className='font-medium'>{theme.label}</div>
                    <div className='text-sm text-gray-500 dark:text-gray-400'>{theme.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * 主题切换器组件
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  onThemeChange,
  showLabel = false,
  label = 'Theme',
  className = '',
  type = 'toggle',
  size = 'md',
  disabled = false,
}) => {
  const { currentTheme, isDark, availableThemes, setMonacoTheme, toggleDarkMode } = useMonacoTheme()

  const icon = useThemeIcon(isDark)

  // 主题变化时调用回调
  useEffect(() => {
    onThemeChange?.(currentTheme)
  }, [currentTheme, onThemeChange])

  const handleToggle = () => {
    toggleDarkMode()
  }

  const handleThemeChange = (theme: string) => {
    setMonacoTheme(theme)
  }

  if (type === 'dropdown') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showLabel && <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>{label}</span>}
        <ThemeDropdown
          currentTheme={currentTheme}
          availableThemes={availableThemes}
          onThemeChange={handleThemeChange}
          disabled={disabled}
          size={size}
        />
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>{label}</span>}

      {type === 'switch' ? (
        <SimpleToggle isDark={isDark} onToggle={handleToggle} disabled={disabled} size={size} />
      ) : (
        <button
          type='button'
          onClick={handleToggle}
          disabled={disabled}
          className={`
            inline-flex items-center justify-center p-2 rounded-lg
            border border-gray-300 dark:border-gray-600
            bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
            hover:bg-gray-50 dark:hover:bg-gray-700
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            transition-colors duration-200
            ${size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span className='text-xl'>{icon}</span>
        </button>
      )}
    </div>
  )
}
